/**
 * BookingLinkCard — Settings surface for the operator's Cal.com / Google
 * Calendar booking URL. When set, Jarvis appends it automatically when
 * a seller says something like "yes let's talk" or "what time works".
 *
 * Deliberately tiny — one input, one save button, a live preview so the
 * operator can see EXACTLY what the seller will read.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock, Check, Loader2, Sparkles, X } from "lucide-react";

export default function BookingLinkCard() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [slugDraft, setSlugDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v3/ai-sdr/booking-link");
      setState(data);
      setDraft(data.booking_link_url || "");
      setSlugDraft(data.booking_slug || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load booking link");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/v3/ai-sdr/booking-link", {
        booking_link_url: draft.trim(),
        booking_slug: slugDraft.trim(),
      });
      setState(data);
      toast.success(data.configured ? "Booking link saved" : "Booking link cleared");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clear = () => setDraft("");
  const dirty = ((draft.trim() || "") !== (state?.booking_link_url || ""))
             || ((slugDraft.trim() || "") !== (state?.booking_slug || ""));

  return (
    <div className="card" data-testid="booking-link-card">
      <div className="flex items-start gap-3">
        <div className={
          "w-10 h-10 rounded-xl grid place-items-center shrink-0 " +
          (state?.configured
            ? "bg-emerald-500/10 text-emerald-700"
            : "bg-amber-500/10 text-amber-700")
        }>
          <CalendarClock size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="label-eyebrow inline-flex items-center gap-1.5">
            <Sparkles size={11} /> Appointment Booking · Jarvis
          </div>
          <h3 className="font-display font-bold text-lg tracking-tight mt-0.5">
            Your booking link
          </h3>
          <p className="text-sm text-[var(--ink-2)] mt-1">
            When a seller signals readiness to talk (&ldquo;yes let&apos;s chat&rdquo;, &ldquo;call me&rdquo;,
            &ldquo;what time works&rdquo;), Jarvis appends this URL so they can self-book.
            Use any tool — Cal.com, Google Calendar, Calendly, Acuity, or a page on your own site.
          </p>
        </div>
        {state?.configured && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-800 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 shrink-0">
            <Check className="w-3 h-3" /> Active
          </span>
        )}
      </div>

      {loading ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-[var(--ink-3)]" data-testid="booking-link-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <div className="mt-4">
            <label className="block">
              <div className="text-[9.5px] uppercase tracking-widest font-black text-slate-500 mb-1">
                Booking URL
              </div>
              <div className="relative">
                <input
                  type="url"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="https://cal.com/tracy-perrin/30min"
                  data-testid="booking-link-input"
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                />
                {draft && (
                  <button
                    type="button"
                    onClick={clear}
                    data-testid="booking-link-clear"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-rose-600"
                    title="Clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </label>
            <div className="text-[10.5px] text-slate-500 mt-1">
              Leave blank to use the built-in booking page below.
            </div>
          </div>

          {/* Phase 3a — public booking slug */}
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/40 p-3">
            <div className="text-[9.5px] uppercase tracking-widest font-black text-amber-700 mb-1">
              Built-in booking page (Mon–Sat 8am–8pm ET · 15/30/60 min)
            </div>
            <div className="flex items-center gap-1.5 text-[13px]">
              <span className="text-slate-500">ispeedtoclose.com/book/</span>
              <input
                type="text"
                value={slugDraft}
                onChange={(e) => setSlugDraft(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                placeholder="your-handle"
                data-testid="booking-slug-input"
                maxLength={40}
                className="flex-1 rounded-md border border-amber-300 bg-white px-2 py-1 text-[13px] focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
            {state?.internal_booking_url && (
              <div className="mt-2 flex items-center gap-2 text-[11px]">
                <a
                  href={state.internal_booking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="booking-slug-preview-link"
                  className="text-amber-700 font-semibold hover:underline break-all"
                >
                  {state.internal_booking_url}
                </a>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(state.internal_booking_url);
                    toast.success("Copied");
                  }}
                  data-testid="booking-slug-copy"
                  className="text-slate-500 hover:text-amber-700"
                  title="Copy"
                >
                  Copy
                </button>
              </div>
            )}
          </div>

          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="booking-link-preview">
            <div className="text-[10px] uppercase tracking-widest font-black text-slate-500 mb-1">
              What Jarvis appends to a warm reply
            </div>
            <div className="text-[12.5px] text-slate-700 italic">
              &ldquo;{(draft || state?.preview_line || "").trim() || "—"}&rdquo;
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty}
              data-testid="booking-link-save"
              className="inline-flex items-center gap-1 rounded-md text-white text-[12px] font-bold px-3 py-1.5 shadow-md hover:opacity-90 disabled:opacity-50"
              style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Save link
            </button>
          </div>
        </>
      )}
    </div>
  );
}
